import { ErrorBoundary } from '@sentry/react';
import { useAtomValue } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { Suspense } from 'react';
import { metricsAtom } from '@/atoms/metric';
import { comparisonTeamIsLoadingAtom } from '@/atoms/queries/team/teamComparison';
import {
  teamLeagueAverageIsLoadingAtom,
  unwrappedTeamRenderedComparisonMinutesPlayedAtom,
  unwrappedTeamRenderedComparisonMinutesPlayed360Atom,
} from '@/atoms/queries/team/teamLeagueAverageRadar';
import {
  teamMetricsAtom,
  unwrappedTeamAggsMinutesPlayedAtom,
  unwrappedTeamAggsMinutesPlayed360Atom,
  loadableTeamRadarDataAtom,
} from '@/atoms/queries/team/teamRadar';
import { radarTemplatesAtom } from '@/atoms/queries/userConfigs';
import {
  radarTableComparisonMetricPercentilesAtom,
  radarTableMetricPercentilesAtom,
  radarTemplateIdAtom,
} from '@/atoms/radar';
import { GeneralFallback } from '@/components/contentState/GeneralFallback';
import { MAX_TEAM_BIO_ROW_COUNT, useTeamBioData } from '@/hooks/radar/useTeamBioData';
import { useRadar360UnavailableMetricKeys } from '@/hooks/useRadar360UnavailableMetricKeys';
import { createPlaceholderData } from './RadarBioTable';
import { RadarTableBase, RadarTableDisplayProps } from './RadarTable';

const TeamRadarTableWithData = ({ isInteractive, showSpokes }: RadarTableDisplayProps) => {
  const radarPercentiles = useAtomValue(radarTableMetricPercentilesAtom);
  const comparisonPercentiles = useAtomValue(radarTableComparisonMetricPercentilesAtom);
  const radarTemplateId = useAtomValue(radarTemplateIdAtom);
  const templates = useAtomValue(radarTemplatesAtom);
  const unavailable360MetricKeys = useRadar360UnavailableMetricKeys();
  const loadableRadarData = useAtomValue(loadableTeamRadarDataAtom);
  const isComparisonDataLoading = useAtomValue(comparisonTeamIsLoadingAtom);
  const isLeagueAverageLoading = useAtomValue(teamLeagueAverageIsLoadingAtom);
  const isFetchingPercentiles =
    loadableRadarData.state === 'loading' || isComparisonDataLoading || isLeagueAverageLoading;

  const metricData = useAtomValue(teamMetricsAtom);
  const minutesPlayed = useAtomValue(unwrappedTeamAggsMinutesPlayedAtom);
  const minutesPlayed360 = useAtomValue(unwrappedTeamAggsMinutesPlayed360Atom);
  const comparisonMinutesPlayed = useAtomValue(unwrappedTeamRenderedComparisonMinutesPlayedAtom);
  const comparisonMinutesPlayed360 = useAtomValue(unwrappedTeamRenderedComparisonMinutesPlayed360Atom);

  const { bioData, entityName, comparisonEntityName } = useTeamBioData();

  return (
    <RadarTableBase
      section="team"
      radarTemplateId={radarTemplateId}
      isInteractive={isInteractive}
      radarPercentiles={radarPercentiles}
      isLoading={isFetchingPercentiles}
      templates={templates}
      unavailable360MetricKeys={unavailable360MetricKeys}
      metricData={metricData}
      minutesPlayed={minutesPlayed}
      minutesPlayed360={minutesPlayed360}
      comparisonPercentiles={comparisonPercentiles}
      comparisonMinutesPlayed={comparisonMinutesPlayed}
      comparisonMinutesPlayed360={comparisonMinutesPlayed360}
      entityName={entityName}
      bioData={bioData}
      comparisonEntityName={comparisonEntityName}
      showSpokes={showSpokes}
    />
  );
};

export const TeamRadarTable = ({ isInteractive, showSpokes }: RadarTableDisplayProps) => (
  <ScopeProvider atoms={[metricsAtom]}>
    <ErrorBoundary fallback={<GeneralFallback type="error" />}>
      <Suspense
        fallback={
          <RadarTableBase
            section="team"
            isLoading
            isInteractive={isInteractive}
            bioData={createPlaceholderData(MAX_TEAM_BIO_ROW_COUNT)}
            showSpokes={showSpokes}
          />
        }
      >
        <TeamRadarTableWithData isInteractive={isInteractive} showSpokes={showSpokes} />
      </Suspense>
    </ErrorBoundary>
  </ScopeProvider>
);
